import { getInsurancesByPriority } from "@/app/directories/companion/(main)/roster/helper";
import { datadogLogs } from "@datadog/browser-logs";
import {
  type RichUnscheduledTreatment,
  reIndexConsumer,
  updateConsumerAndEHRPatientData,
  updateUserAndEhrPatient,
  writeConsumerNote,
} from "@frontend/lib/actions/actions";
import {
  type ConsumerUpdateInput,
  InsurancesStatusEnum,
  type LanguageCode,
  type UpdateUserInput,
  type UserUpdateInput,
} from "@frontend/lib/graphql/generated-gql";
import type { GetUserByUserIdQueryUserWithConsumer } from "@frontend/lib/graphql/gql-types";
import type {
  GetConsumerByConsumerIdQueryConsumer as Consumer,
  GetUserByUserIdQueryUser as User,
} from "@frontend/lib/graphql/gql-types";
import type {
  AppointmentSchema,
  ProcedureSchema,
} from "@frontend/lib/schemas/appointmentSchema";
import {
  type AdditionalPatientData,
  type FormInsuranceData,
  type InsuranceData,
  type NewPatientData,
  formInsuranceSchema,
} from "@frontend/lib/schemas/clientSchema";
import { isDeepEqual } from "@frontend/shared/lib/utils";
import { format, toZonedTime } from "date-fns-tz";
import type {
  FieldErrors,
  FormState,
  UseFormGetValues,
  UseFormTrigger,
} from "react-hook-form";
import {
  triggerPatientUpdateEHRFailureToast,
  triggerPatientUpdateSuccessToast,
} from "../../ToastHelpers";

export const getAffectedTreatments = (
  appointments: AppointmentSchema[] | null | undefined,
  unscheduledTreatments: RichUnscheduledTreatment[] | null | undefined,
  repriceableInsurances: FormInsuranceData[],
  deletedInsurances: FormInsuranceData[],
  newInsurancesExist: boolean,
): ProcedureSchema[] => {
  // Get all affected insurance IDs (both repriceable and deleted)
  const affectedInsuranceIds = [
    ...(repriceableInsurances?.map((insurance) => insurance.id) ?? []),
    ...(deletedInsurances?.map((insurance) => insurance.id) ?? []),
  ];
  if (affectedInsuranceIds?.length === 0 && !newInsurancesExist) {
    return [];
  }

  // Get treatments from appointments
  const scheduledTreatments =
    appointments?.flatMap(
      (appointment) =>
        appointment.slots?.flatMap((slot) => slot.procedures ?? []) ?? [],
    ) ?? [];

  // Get treatments from unscheduled treatments
  const filteredUnscheduledTreatments = (unscheduledTreatments ?? []).map(
    (b) => ({
      ...b,
      boughtTreatmentId: b.id,
      label: b.name,
      selectedPaymentType: b.paymentType,
      discountCodeApplied: b.discountCodeApplied ?? undefined,
      overrideReason: b.overrideReason ?? undefined,
      lastUpdatedBy: b.lastUpdatedBy ?? undefined,
    }),
  );

  // Combine both arrays of treatments
  return [...scheduledTreatments, ...filteredUnscheduledTreatments];
};

interface GetModifiedInsurancesParams {
  insurances: FormInsuranceData[] | undefined | null;
  primaryInsurance: FormInsuranceData | undefined;
  secondaryInsurance: FormInsuranceData | undefined;
  tertiaryInsurance: FormInsuranceData | undefined;
  errors: FieldErrors<NewPatientData>;
  isInlineOverride?: boolean;
  isEditingManualVerification: Record<
    "primaryInsurance" | "secondaryInsurance" | "tertiaryInsurance",
    boolean
  >;
}

type InsuranceChanges = {
  modified: FormInsuranceData[];
  newInsurances: FormInsuranceData[];
  repriceable: FormInsuranceData[];
};

export const getInsuranceDelta = (
  serverInsurance: FormInsuranceData,
  formInsurance: FormInsuranceData,
) => {
  // Create a list of keys to compare
  const keysToCompare = [
    "memberId",
    "state",
    "payer",
    "status",
    "insuredFirstName",
    "insuredLastName",
    "subscriberInfo",
    "otherInsurances",
    "planStartDate",
    "planEndDate",
    "pcpName",
    "priority",
    "planName",
    "pcpCopayAmount",
    "lastUpdatedBy",
    "specialistCopayAmount",
    "pcpCoinsurancePercentage",
    "specialistCoinsurancePercentage",
  ];

  const delta = keysToCompare.reduce(
    (acc, key) => {
      const keyTyped = key as keyof FormInsuranceData;
      const oldValue = serverInsurance[keyTyped];
      const newValue = formInsurance?.[keyTyped];

      const keyHasChange =
        !isDeepEqual(oldValue, newValue) ||
        (oldValue === null && newValue !== null);

      if (keyHasChange) {
        acc[key] = {
          from: oldValue,
          to: newValue,
        };
      }
      return acc;
    },
    // biome-ignore lint/suspicious/noExplicitAny: <explanation>
    {} as Record<string, { from: any; to: any }>,
  );

  return delta;
};

export const willAffectRelatedConsumers = (
  formState: FormState<Partial<NewPatientData | AdditionalPatientData>>,
  userInfo: GetUserByUserIdQueryUserWithConsumer,
) => {
  const { dirtyFields } = formState;
  return (
    ["dob", "email", "primaryLanguage"].some((field) => dirtyFields[field]) &&
    userInfo.consumers.length > 1
  );
};

/**
 * Compares two sets of insurance data to identify modified entries.
 * An insurance entry is considered modified if:
 * 1. It exists in both sets (matched by ID)
 * 2. Its data has changed (compared via JSON.stringify)
 * 3. It's not in the deletedInsuranceIds set
 *
 * @param params Configuration object containing required parameters
 * @param params.storedInsurances - The cached/stored insurance entries to compare against
 * @param params.newInsurances - The new set of insurance entries to check
 * @param params.deletedInsuranceIds - Array of insurance IDs that have been marked for deletion
 * @returns Array of modified insurance entries
 * @throws Error if insurance entries are missing required ID field
 */
export const getModifiedInsurances = ({
  insurances,
  primaryInsurance,
  secondaryInsurance,
  tertiaryInsurance,
  errors,
  isInlineOverride,
  isEditingManualVerification,
}: GetModifiedInsurancesParams): InsuranceChanges => {
  const buildOverride = (insurance?: FormInsuranceData) => {
    return {
      planStartDate: insurance?.planStartDate,
      planEndDate: insurance?.planEndDate,
      pcpCopayAmount: insurance?.pcpCopayAmount,
      specialistCopayAmount: insurance?.specialistCopayAmount,
      pcpCoinsurancePercentage: insurance?.pcpCoinsurancePercentage,
      specialistCoinsurancePercentage:
        insurance?.specialistCoinsurancePercentage,
    };
  };

  const overridesMap = {
    primaryInsurance: buildOverride(primaryInsurance),
    secondaryInsurance: buildOverride(secondaryInsurance),
    tertiaryInsurance: buildOverride(tertiaryInsurance),
  };

  const result: InsuranceChanges = {
    modified: [],
    newInsurances: [],
    repriceable: [],
  };

  // Helper function to find matching insurance
  const findMatchingInsurance = (formInsurance: FormInsuranceData) => {
    return insurances?.find(
      (ins) => formInsurance.id && ins.id === formInsurance.id,
    );
  };

  const hasChangesRepriceable = (
    serverInsurance: FormInsuranceData,
    formInsurance: FormInsuranceData,
  ): boolean => {
    return (
      serverInsurance.memberId !== formInsurance.memberId ||
      serverInsurance.state !== formInsurance.state ||
      serverInsurance.payer !== formInsurance.payer
    );
  };

  const hasChanges = (
    serverInsurance: FormInsuranceData,
    formInsurance: FormInsuranceData,
  ): boolean => {
    return (
      hasChangesRepriceable(serverInsurance, formInsurance) ||
      Object.keys(getInsuranceDelta(serverInsurance, formInsurance)).length > 0
    );
  };

  const processInsurance = (
    formInsurance: FormInsuranceData | null,
    errorKey: "primaryInsurance" | "secondaryInsurance" | "tertiaryInsurance",
    isPrimary = false,
  ) => {
    if (!formInsurance) return;

    const isInsuranceManuallyOverriden = !!Object.values(
      overridesMap[errorKey],
    ).some((key) => key);

    const noErrorsOrOverriden = !!(
      !errors?.[errorKey] ||
      (errors[errorKey] && isInsuranceManuallyOverriden)
    );

    if (
      isEditingManualVerification[errorKey] ||
      isInlineOverride ||
      isInsuranceManuallyOverriden
    ) {
      formInsurance.status = InsurancesStatusEnum.Manuallyverified;

      const parsedPlanStartDate = formInsurance?.planStartDate;
      const parsePlanEndDate = formInsurance?.planEndDate;

      formInsurance.planStartDate = parsedPlanStartDate;
      formInsurance.planEndDate = parsePlanEndDate;
    } else if (formInsurance.status === InsurancesStatusEnum.Unverified) {
      formInsurance.planStartDate = null;
      formInsurance.planEndDate = null;
    }

    if (!insurances?.length) {
      if (isInsuranceValid(formInsurance) && noErrorsOrOverriden) {
        result.newInsurances.push(formInsurance as FormInsuranceData);
      }
      return;
    }

    const matchingInsurance = findMatchingInsurance(formInsurance);
    if (!matchingInsurance) {
      if (isInsuranceValid(formInsurance) && noErrorsOrOverriden) {
        result.newInsurances.push(formInsurance as FormInsuranceData);
      }
    } else {
      const processedInsurance = {
        ...matchingInsurance,
        ...formInsurance,
        isPrimaryInsurance: isPrimary,
      } as FormInsuranceData;

      if (
        hasChanges(matchingInsurance, processedInsurance) &&
        isInsuranceValid(processedInsurance)
      ) {
        result.modified.push(processedInsurance);
        if (hasChangesRepriceable(matchingInsurance, processedInsurance)) {
          result.repriceable.push(processedInsurance);
        }
      }
    }
  };

  // Process all insurances
  if (primaryInsurance) {
    processInsurance(primaryInsurance, "primaryInsurance", true);
  }
  if (secondaryInsurance) {
    processInsurance(secondaryInsurance, "secondaryInsurance");
  }
  if (tertiaryInsurance) {
    processInsurance(tertiaryInsurance, "tertiaryInsurance");
  }

  return result;
};

/**
 * Validate consumer information and returns the current object. Used for insurance check
 * @param trigger
 * @param getValues
 * @returns
 */
export const getConsumerToCheck = async (
  trigger: UseFormTrigger<Partial<NewPatientData | AdditionalPatientData>>,
  getValues: UseFormGetValues<Partial<NewPatientData | AdditionalPatientData>>,
  consumerId: string,
) => {
  const isConsumerValid = await trigger([
    "consumerFirstName",
    "consumerLastName",
    "dob",
  ]);

  if (isConsumerValid) {
    const firstname = getValues("consumerFirstName");
    const lastname = getValues("consumerLastName");
    const dobString = getValues("dob");
    return {
      id: consumerId,
      firstname,
      lastname,
      dob: dobString,
    };
  }
  return null;
};

export const getAreRunCheckFieldsDirty = (
  // biome-ignore lint/suspicious/noExplicitAny: <explanation>
  dirtyFields: Partial<Readonly<any>>,
  primaryInsurance: Partial<FormInsuranceData> | undefined,
  secondaryInsurance: Partial<FormInsuranceData> | undefined,
  tertiaryInsurance: Partial<FormInsuranceData> | undefined,
) => {
  const hasInsurance =
    primaryInsurance || secondaryInsurance || tertiaryInsurance;

  const excludeManualVerificationFields = (
    // biome-ignore lint/suspicious/noExplicitAny: <explanation>
    dirtyFields: any,
    insuranceField:
      | "primaryInsurance"
      | "secondaryInsurance"
      | "tertiaryInsurance",
  ) => {
    // whitelist the manual verification fields
    return Object.entries(dirtyFields[insuranceField]).some(([key, value]) => {
      const manualVerificationFields = [
        "pcpCopayAmount",
        "specialistCopayAmount",
        "pcpCoinsurancePercentage",
        "specialistCoinsurancePercentage",
        "planStartDate",
        "planEndDate",
      ];
      if (manualVerificationFields.includes(key)) {
        return false;
      }
      return value;
    });
  };

  const primaryInsuranceDirty = dirtyFields.primaryInsurance
    ? excludeManualVerificationFields(dirtyFields, "primaryInsurance")
    : false;

  // Same for secondary insurance
  const secondaryInsuranceDirty = dirtyFields.secondaryInsurance
    ? excludeManualVerificationFields(dirtyFields, "secondaryInsurance")
    : false;

  // Same for tertiary insurance
  const tertiaryInsuranceDirty = dirtyFields.tertiaryInsurance
    ? excludeManualVerificationFields(dirtyFields, "tertiaryInsurance")
    : false;

  const primaryIsUsingDifferentInsuredName = [
    primaryInsurance?.insuredFirstName,
    primaryInsurance?.insuredLastName,
  ].every((name) => name !== null && name !== "" && name !== undefined);

  const primaryInsuredNameChanged =
    dirtyFields.primaryInsurance &&
    (dirtyFields.primaryInsurance?.insuredFirstName ||
      dirtyFields.primaryInsurance?.insuredLastName);

  const secondaryIsUsingDifferentInsuredName = [
    secondaryInsurance?.insuredFirstName,
    secondaryInsurance?.insuredLastName,
  ].every((name) => name !== null && name !== "" && name !== undefined);

  const secondaryInsuredNameChanged =
    dirtyFields.secondaryInsurance &&
    (dirtyFields.secondaryInsurance?.insuredFirstName ||
      dirtyFields.secondaryInsurance?.insuredLastName);

  const tertiaryIsUsingDifferentInsuredName = [
    tertiaryInsurance?.insuredFirstName,
    tertiaryInsurance?.insuredLastName,
  ].every((name) => name !== null && name !== "" && name !== undefined);

  const tertiaryInsuredNameChanged =
    dirtyFields.tertiaryInsurance &&
    (dirtyFields.tertiaryInsurance?.insuredFirstName ||
      dirtyFields.tertiaryInsurance?.insuredLastName);

  const primaryNameChanged =
    primaryInsurance &&
    (primaryIsUsingDifferentInsuredName
      ? primaryInsuredNameChanged
      : dirtyFields.consumerFirstName || dirtyFields.consumerLastName);

  const secondaryNameChanged =
    secondaryInsurance &&
    (secondaryIsUsingDifferentInsuredName
      ? secondaryInsuredNameChanged
      : dirtyFields.consumerFirstName || dirtyFields.consumerLastName);

  const tertiaryNameChanged =
    tertiaryInsurance &&
    (tertiaryIsUsingDifferentInsuredName
      ? tertiaryInsuredNameChanged
      : dirtyFields.consumerFirstName || dirtyFields.consumerLastName);

  return (
    hasInsurance &&
    (dirtyFields.dob ||
      primaryNameChanged ||
      secondaryNameChanged ||
      tertiaryNameChanged ||
      primaryInsuranceDirty ||
      secondaryInsuranceDirty ||
      tertiaryInsuranceDirty)
  );
};

export type PatientUpdateFailures = {
  pharmacy: boolean;
  pcp: boolean;
  athena: boolean;
  insurance: boolean;
  user: boolean;
  redox: boolean;
};

export const processUpdates = async (
  consumerInfo: Consumer,
  userInfo: User,
  formData: NewPatientData | AdditionalPatientData,
): Promise<string[]> => {
  const isAdditionalPatientData = (
    formData: NewPatientData | AdditionalPatientData,
  ) => {
    return "maritalStatus" in formData;
  };

  const failures: PatientUpdateFailures = {
    pharmacy: false,
    pcp: false,
    athena: false, // patientAthenaUpdate
    insurance: false,
    user: false, // user
    redox: false, // patientUpdate
  };

  datadogLogs.logger.info(
    "PatientHeaderHelper: processing updates for consumer",
    {
      consumerId: consumerInfo.id,
      userId: userInfo.id,
    },
  );

  const hasUserUpdate =
    userInfo.email !== formData.email ||
    userInfo.phone !== formData.phone ||
    userInfo.primaryLanguage !== formData.primaryLanguage;

  if (hasUserUpdate) {
    const userUpdateInput: UserUpdateInput = {};

    if (userInfo.email !== formData.email) {
      userUpdateInput.email = { set: formData.email };
    }
    if (userInfo.phone !== formData.phone) {
      userUpdateInput.phone = { set: formData.phone };
    }
    if (userInfo.primaryLanguage !== formData.primaryLanguage) {
      userUpdateInput.primaryLanguage = {
        set: formData.primaryLanguage as LanguageCode,
      };
    }

    const gqlUserUpdateInput: UpdateUserInput = {
      id: userInfo.id,
      user: userUpdateInput,
    };

    const userUpdateResponse =
      await updateUserAndEhrPatient(gqlUserUpdateInput);

    if (userUpdateResponse.consumersWithEhrWriteErrors.length > 0) {
      failures.user = true;
    }
  }

  const hasAnyNameChange =
    consumerInfo.firstname !== formData.consumerFirstName ||
    consumerInfo.lastname !== formData.consumerLastName ||
    consumerInfo.preferredFirstName !== formData.preferredFirstName ||
    consumerInfo.preferredLastName !== formData.preferredLastName;

  const transformedData: ConsumerUpdateInput = {};
  if (consumerInfo.firstname !== formData.consumerFirstName)
    transformedData.firstname = { set: formData.consumerFirstName.trim() };
  if (consumerInfo.lastname !== formData.consumerLastName)
    transformedData.lastname = { set: formData.consumerLastName.trim() };

  // Compare DOB as formatted strings to avoid timezone parsing issues
  const serverDobFormatted = format(
    toZonedTime(new Date(consumerInfo.dob), "UTC"),
    "MM/dd/yyyy",
  );
  if (serverDobFormatted !== formData.dob) {
    // Convert "MM/dd/yyyy" to UTC midnight
    const [month, day, year] = formData.dob.split("/");
    const utcDob = new Date(`${year}-${month}-${day}T00:00:00Z`);
    transformedData.dob = { set: utcDob };
  }

  if (consumerInfo.sex !== formData.sex)
    transformedData.sex = { set: formData.sex };
  if (consumerInfo.city !== formData.city)
    transformedData.city = { set: formData.city };
  if (consumerInfo.country !== formData.country)
    transformedData.country = { set: formData.country };
  if (consumerInfo.state !== formData.state)
    transformedData.state = { set: formData.state };
  if (consumerInfo.streetAddress !== formData.streetAddress)
    transformedData.streetAddress = { set: formData.streetAddress };
  if (consumerInfo.zip !== formData.zip)
    transformedData.zip = { set: formData.zip };
  if (consumerInfo.preferredFirstName !== formData.preferredFirstName)
    transformedData.preferredFirstName = {
      set: formData.preferredFirstName?.trim(),
    };
  if (consumerInfo.preferredLastName !== formData.preferredLastName)
    transformedData.preferredLastName = {
      set: formData.preferredLastName?.trim(),
    };

  if (isAdditionalPatientData(formData)) {
    const additionalData = formData as AdditionalPatientData;

    if (additionalData.maritalStatus !== consumerInfo.maritalStatus) {
      transformedData.maritalStatus = { set: additionalData.maritalStatus };
    }

    if (additionalData.ethnicity !== consumerInfo.ethnicity) {
      transformedData.ethnicity = { set: additionalData.ethnicity };
    }

    if (additionalData.race === null) {
      transformedData.race = { set: null };
    } else {
      // Sort both arrays and compare to check if they have the same values regardless of order
      const currentRace = consumerInfo.race?.slice().sort() || [];
      const newRace = additionalData.race.slice().sort();

      if (
        currentRace.length !== newRace.length ||
        !currentRace.every((race, index) => race === newRace[index])
      ) {
        transformedData.race = { set: additionalData.race };
      }
    }

    if (
      additionalData.pharmacy === null ||
      (additionalData.pharmacy &&
        additionalData.pharmacy.name === null &&
        additionalData.pharmacy.phone === null &&
        additionalData.pharmacy.address === null &&
        additionalData.pharmacy.ehrId === null)
    ) {
      if (
        consumerInfo.pharmacyName !== null ||
        consumerInfo.pharmacyPhone !== null ||
        consumerInfo.pharmacyAddress !== null ||
        consumerInfo.pharmacyEhrId !== null
      ) {
        transformedData.pharmacyName = { set: null };
        transformedData.pharmacyPhone = { set: null };
        transformedData.pharmacyAddress = null;
        transformedData.pharmacyEhrId = { set: null };
      }
    } else {
      if (additionalData.pharmacy) {
        if (additionalData.pharmacy.name !== consumerInfo.pharmacyName) {
          transformedData.pharmacyName = { set: additionalData.pharmacy.name };
        }
        if (additionalData.pharmacy.phone !== consumerInfo.pharmacyPhone) {
          transformedData.pharmacyPhone = {
            set: additionalData.pharmacy.phone,
          };
        }
        if (
          !isDeepEqual(
            additionalData.pharmacy.address,
            consumerInfo.pharmacyAddress,
          )
        ) {
          transformedData.pharmacyAddress = additionalData.pharmacy.address;
        }
        if (additionalData.pharmacy.ehrId !== consumerInfo.pharmacyEhrId) {
          transformedData.pharmacyEhrId = {
            set: additionalData.pharmacy.ehrId,
          };
        }
      }
    }

    if (additionalData.referralSource) {
      if (additionalData.referralSource.id !== consumerInfo.referralSourceId) {
        transformedData.referralSourceId = {
          set: additionalData.referralSource.id,
        };
      }
      if (
        additionalData.referralSource.detail !==
        consumerInfo.referralSourceDetail
      ) {
        transformedData.referralSourceDetail = {
          set: additionalData.referralSource.detail,
        };
      }
      if (
        additionalData.referralSource.option !== consumerInfo.referralSource
      ) {
        transformedData.referralSource = {
          set: additionalData.referralSource.option,
        };
      }
    }

    if (
      additionalData.pcp === null ||
      (additionalData.pcp &&
        additionalData.pcp.firstName === null &&
        additionalData.pcp.lastName === null &&
        additionalData.pcp.address === null &&
        additionalData.pcp.ehrId === null)
    ) {
      if (
        consumerInfo.pcpFirstName !== null ||
        consumerInfo.pcpLastName !== null ||
        consumerInfo.pcpPhone !== null ||
        consumerInfo.pcpAddress !== null ||
        consumerInfo.pcpEhrId !== null
      ) {
        transformedData.pcpFirstName = { set: null };
        transformedData.pcpLastName = { set: null };
        transformedData.pcpPhone = { set: null };
        transformedData.pcpAddress = null;
        transformedData.pcpEhrId = { set: null };
      }
    } else {
      if (additionalData.pcp) {
        if (additionalData.pcp.firstName !== consumerInfo.pcpFirstName) {
          transformedData.pcpFirstName = { set: additionalData.pcp.firstName };
        }
        if (additionalData.pcp.lastName !== consumerInfo.pcpLastName) {
          transformedData.pcpLastName = { set: additionalData.pcp.lastName };
        }
        if (additionalData.pcp.phone !== consumerInfo.pcpPhone) {
          transformedData.pcpPhone = { set: additionalData.pcp.phone };
        }
        if (!isDeepEqual(additionalData.pcp.address, consumerInfo.pcpAddress)) {
          transformedData.pcpAddress = additionalData.pcp.address;
        }
        if (additionalData.pcp.ehrId !== consumerInfo.pcpEhrId) {
          transformedData.pcpEhrId = { set: additionalData.pcp.ehrId };
        }
      }
    }

    if (
      additionalData.emergencyContact === null ||
      (additionalData.emergencyContact &&
        additionalData.emergencyContact.firstName === null &&
        additionalData.emergencyContact.lastName === null &&
        additionalData.emergencyContact.phone === null &&
        additionalData.emergencyContact.relationship === null)
    ) {
      if (
        consumerInfo.emergencyContactFirstName !== null ||
        consumerInfo.emergencyContactLastName !== null ||
        consumerInfo.emergencyContactPhone !== null ||
        consumerInfo.emergencyContactRelationship !== null
      ) {
        transformedData.emergencyContactFirstName = { set: null };
        transformedData.emergencyContactLastName = { set: null };
        transformedData.emergencyContactPhone = { set: null };
        transformedData.emergencyContactRelationship = { set: null };
      }
    } else {
      if (additionalData.emergencyContact) {
        if (
          additionalData.emergencyContact.firstName !==
          consumerInfo.emergencyContactFirstName
        ) {
          transformedData.emergencyContactFirstName = {
            set: additionalData.emergencyContact.firstName,
          };
        }
        if (
          additionalData.emergencyContact.lastName !==
          consumerInfo.emergencyContactLastName
        ) {
          transformedData.emergencyContactLastName = {
            set: additionalData.emergencyContact.lastName,
          };
        }
        if (
          additionalData.emergencyContact.phone !==
          consumerInfo.emergencyContactPhone
        ) {
          transformedData.emergencyContactPhone = {
            set: additionalData.emergencyContact.phone,
          };
        }
        if (
          additionalData.emergencyContact.relationship !==
          consumerInfo.emergencyContactRelationship
        ) {
          transformedData.emergencyContactRelationship = {
            set: additionalData.emergencyContact.relationship,
          };
        }
      }
    }
  }

  const hasConsumerUpdate = Object.keys(transformedData).length > 0;

  if (hasConsumerUpdate || hasUserUpdate) {
    const updateResponse = await updateConsumerAndEHRPatientData(
      consumerInfo.id,
      transformedData,
    );

    // patientAthenaUpdateResponse -> has emergency contact, preferredName, and referral source
    if (updateResponse.ehrUpdateResponse) {
      if (
        updateResponse.ehrUpdateResponse.patientUpdateResponse !== null ||
        updateResponse.ehrUpdateResponse.pharmacyUpdateResponse !== null ||
        updateResponse.ehrUpdateResponse.pcpUpdateResponse !== null ||
        updateResponse.ehrUpdateResponse.patientAthenaUpdateResponse !== null
      ) {
        if (updateResponse.ehrUpdateResponse.pharmacyUpdateResponse?.error) {
          failures.pharmacy = true;
        }
        if (updateResponse.ehrUpdateResponse.pcpUpdateResponse?.error) {
          failures.pcp = true;
        }
        if (
          updateResponse.ehrUpdateResponse.patientAthenaUpdateResponse?.error
        ) {
          failures.athena = true;
        }
        if (updateResponse.ehrUpdateResponse.patientUpdateResponse?.error) {
          failures.redox = true;
        }
      }
    }
    if (hasAnyNameChange) {
      if (userInfo.orgId) {
        reIndexConsumer({
          consumerId: consumerInfo.id,
          document: {
            orgIds: [userInfo.orgId],
            firstname: formData.consumerFirstName,
            preferredFirstName: formData.preferredFirstName ?? undefined,

            lastname: formData.consumerLastName,
            preferredLastName: formData.preferredLastName ?? undefined,
          },
        });
      }
    }
  }

  if (
    failures.insurance ||
    failures.pharmacy ||
    failures.pcp ||
    failures.athena ||
    failures.redox
  ) {
    triggerPatientUpdateEHRFailureToast(failures, Object.keys(transformedData));
  } else {
    const hasChanges = hasUserUpdate || hasConsumerUpdate;
    if (hasChanges) {
      triggerPatientUpdateSuccessToast();
    }
  }

  // if we need to return more info from this in future, then don't just return an array of strings
  return [];
};

export const buildDefaultValuesForHeader = (
  userInfo: User,
  consumerInfo: Consumer,
  insurances: InsuranceData[] | undefined | null,
): NewPatientData => {
  const [primaryInsurance, secondaryInsurance, tertiaryInsurance] =
    getInsurancesByPriority(insurances ?? []);

  return {
    dob: format(toZonedTime(new Date(consumerInfo?.dob), "UTC"), "MM/dd/yyyy"),
    phone: userInfo.phone ?? "",
    email: userInfo.email ?? "",
    fname: userInfo.firstname,
    lname: userInfo.lastname,
    primaryLanguage: userInfo.primaryLanguage,
    city: consumerInfo.city ?? "",
    country: consumerInfo.country ?? "",
    state: consumerInfo.state ?? "",
    streetAddress: consumerInfo.streetAddress ?? "",
    zip: consumerInfo.zip ?? "",
    consumerFirstName: consumerInfo.firstname,
    preferredFirstName: consumerInfo.preferredFirstName,
    consumerLastName: consumerInfo.lastname,
    preferredLastName: consumerInfo.preferredLastName,
    sex: consumerInfo.sex ?? "",
    ...(primaryInsurance && {
      primaryInsurance: {
        ...omitFields(primaryInsurance),
        priority: 1,
        memberId: primaryInsurance.memberId ?? "",
        state: primaryInsurance.state ?? "",
        payer: primaryInsurance.payer ?? "",
        planEndDate: primaryInsurance.planEndDate ?? null,
        planStartDate: primaryInsurance.planStartDate ?? null,
        pcpCopayAmount: primaryInsurance.pcpCopayAmount ?? null,
        specialistCopayAmount: primaryInsurance.specialistCopayAmount ?? null,
        pcpCoinsurancePercentage:
          primaryInsurance.pcpCoinsurancePercentage ?? null,
      },
    }),
    ...(secondaryInsurance && {
      secondaryInsurance: {
        ...omitFields(secondaryInsurance),
        priority: 2,
        memberId: secondaryInsurance.memberId ?? "",
        state: secondaryInsurance.state ?? "",
        payer: secondaryInsurance.payer ?? "",
        planEndDate: secondaryInsurance.planEndDate ?? null,
        planStartDate: secondaryInsurance.planStartDate ?? null,
        pcpCopayAmount: secondaryInsurance.pcpCopayAmount ?? null,
        specialistCopayAmount: secondaryInsurance.specialistCopayAmount ?? null,
        pcpCoinsurancePercentage:
          secondaryInsurance.pcpCoinsurancePercentage ?? null,
      },
    }),
    ...(tertiaryInsurance && {
      tertiaryInsurance: {
        ...omitFields(tertiaryInsurance),
        priority: 3,
        memberId: tertiaryInsurance.memberId ?? "",
        state: tertiaryInsurance.state ?? "",
        payer: tertiaryInsurance.payer ?? "",
        planEndDate: tertiaryInsurance.planEndDate ?? null,
        planStartDate: tertiaryInsurance.planStartDate ?? null,
        pcpCopayAmount: tertiaryInsurance.pcpCopayAmount ?? null,
        specialistCopayAmount: tertiaryInsurance.specialistCopayAmount ?? null,
        pcpCoinsurancePercentage:
          tertiaryInsurance.pcpCoinsurancePercentage ?? null,
      },
    }),
  };
};

/**
 * For secondary insurance, use a schema with a looser manually verified criteria
 * @param insurance
 * @param isPrimary
 * @returns
 */
export const isInsuranceValid = (
  insurance: Partial<FormInsuranceData> | undefined,
) => {
  return formInsuranceSchema.safeParse(insurance).success;
};

export const sexOptionsObject = (sex: string | null | undefined) => {
  if (sex === null || sex === undefined) return undefined;
  return sex === "Male" ? { label: "Male", id: 0 } : { label: "Female", id: 1 };
};

export const languageOptions = new Map([
  [
    "en",
    {
      languageCode: "en",
      englishLabel: "English",
      localizedLabel: "English",
      emoji: "🇺🇸",
    },
  ],
  [
    "es",
    {
      languageCode: "es",
      englishLabel: "Spanish",
      localizedLabel: "Español",
      emoji: "🇪🇸",
    },
  ],
  [
    "ru",
    {
      languageCode: "ru",
      englishLabel: "Russian",
      localizedLabel: "Русский",
      emoji: "🇷🇺",
    },
  ],
  [
    "zh",
    {
      languageCode: "zh",
      englishLabel: "Chinese (Simplified)",
      localizedLabel: "中文",
      emoji: "🇨🇳",
    },
  ],
  [
    "zh_HK",
    {
      languageCode: "zh_HK",
      englishLabel: "Chinese (Traditional)",
      localizedLabel: "中文",
      emoji: "🇭🇰",
    },
  ],
  [
    "pt",
    {
      languageCode: "pt",
      englishLabel: "Portuguese",
      localizedLabel: "Português",
      emoji: "🇵🇹",
    },
  ],
  [
    "el",
    {
      languageCode: "el",
      englishLabel: "Greek",
      localizedLabel: "Ελληνικά",
      emoji: "🇬🇷",
    },
  ],
  [
    "ht",
    {
      languageCode: "ht",
      englishLabel: "Haitian Creole",
      localizedLabel: "Kreyòl ayisyen",
      emoji: "🇭🇹",
    },
  ],
  [
    "hi",
    {
      languageCode: "hi",
      englishLabel: "Hindi",
      localizedLabel: "हिन्दी",
      emoji: "🇮🇳",
    },
  ],
  [
    "fr",
    {
      languageCode: "fr",
      englishLabel: "French",
      localizedLabel: "Français",
      emoji: "🇫🇷",
    },
  ],
]);

// todo: all places where we call this should filter for org supported languages
export const languageOptionsWithEnglishLabels = [
  ...languageOptions.values(),
].map((option) => ({ label: option.englishLabel, value: option.languageCode }));

export const languageOptionsWithLocalizedLabels = [
  ...languageOptions.values(),
].map((option) => ({
  label: `${option.localizedLabel} ${option.emoji}`,
  value: option.languageCode,
}));

// helper function to remove fields that are not stored on insurance object
// biome-ignore lint/suspicious/noExplicitAny: <explanation>
export const omitFields = (insurance: any) => {
  if (!insurance) return insurance;
  const { __typename, inNetwork, hasBenefits, ...rest } = insurance;
  return rest;
};

export const prepInsuranceForGqlInput = (
  // biome-ignore lint/suspicious/noExplicitAny: <explanation>
  insurance: any,
  // biome-ignore lint/suspicious/noExplicitAny: <explanation>
  delta: Record<string, { from: any; to: any }> | null,
  createOperation = false,
) => {
  if (!insurance) return insurance;
  const rest = omitFields(insurance);
  // remove ids bc we can't update it
  const { id, consumerId, ...restWithoutIds } = rest;
  const { payer, ...restWithoutPayer } = restWithoutIds;

  // Create new object with wrapped values
  // biome-ignore lint/suspicious/noExplicitAny: <explanation>
  const wrappedInsurance: any = {};

  const input = createOperation ? restWithoutIds : restWithoutPayer;
  // Iterate through all properties
  for (const [key, value] of Object.entries(input)) {
    // Skip subscriberInfo and otherInsurances
    if (key === "subscriberInfo" || key === "otherInsurances") {
      wrappedInsurance[key] = value;
      continue;
    }
    if (!createOperation && delta) {
      // Only include fields that have changed according to delta
      if (key in delta) {
        if (value === null) {
          wrappedInsurance[key] = { set: null };
        } else {
          wrappedInsurance[key] = { set: value };
        }
      }
    } else {
      wrappedInsurance[key] = value;
    }
  }

  return wrappedInsurance;
};

/**
 * Writes consumer notes for insurance overrides
 */
export const writeConsumerNotesForOverrides = async (
  adminId: string,
  consumer: Consumer,
  primaryInsurance?: Partial<FormInsuranceData>,
  secondaryInsurance?: Partial<FormInsuranceData>,
  tertiaryInsurance?: Partial<FormInsuranceData>,
) => {
  const insurances = [
    { type: "primary", data: primaryInsurance },
    { type: "secondary", data: secondaryInsurance },
    { type: "tertiary", data: tertiaryInsurance },
  ];

  const consumerNotes = consumer.consumerNotes ?? [];
  const consumerHasNote =
    consumerNotes.length > 0 && consumerNotes[0].note.length > 0;

  let note = consumerHasNote ? consumerNotes[0].note : "";

  for (const insurance of insurances) {
    const { type, data } = insurance;

    if (data) {
      const consumerNote = `
        Override information - ${type} insurance
        PCP copay  - ${data.pcpCopayAmount || "N/A"}
        Specialist Copay - ${data.specialistCopayAmount || "N/A"}
        Coinsurance - ${data.pcpCoinsurancePercentage || "N/A"}
        Updated ${new Date()}
      `;

      note = `${note} 
      ${consumerNote}
      `;
    }
  }

  await writeConsumerNote(adminId, consumer.id, note);
};
